<template>
    <div v-if="isShown" :class="(this.cssModifier ? `modal modal--${this.cssModifier}` : 'modal')"
         @click="close"
         @keyup.esc="close">
        <div ref="modalWrapper"
             class="modal__content"
             @click="doNothing"
             @keyup.esc="close">
            <i ref="closeModal"
               :class="`modal__content__close fas ${this.scheme.resources.brandedImagery.closeIcon}`"
               tabindex="0"
               role="button"
               @click="close"
               @keyup.esc="close"
               @keyup.enter="close" />
            <component :is="modalContentComponent"
                       :def="modalInfoData && modalInfoData.id ? String(modalInfoData.id) : 'mdl'"
                       :info="modalInfoData"
                       :nested-component="nestedComponent"
                       @edit="onEdit"
                       @cancel="onCancel"
                       @save="onSave" />
        </div>
        <slot />
        <span class="--mobile --js-exclude-tab" tabindex="0" />
    </div>
</template>

<script>

import _ from 'lodash'
import Window from 'window-wrapper'

import LoopedTabbingControl from '../looped-tabbing-control'

const loggingCategory = 'Modal'

export default {

    mixins: [
        LoopedTabbingControl,
    ],

    data() {
        return {
            isShown: false,
            modalContentComponent: undefined,
            modalInfoData: undefined,
            nestedComponent: undefined,
            preventAdditionalActions: false,
            additionalCloseActions: undefined,
            additionalEditActions: undefined,
            additionalCancelActions: undefined,
            additionalSaveActions: undefined,
            cssModifier: '',
        }
    },

    computed: {

        /**
         * @returns {Scheme}
         */
        scheme() {
            return this.$scheme
        },
    },

    watch: {

        tabbables(newValue, oldValue) {
            if (newValue !== oldValue) {
                if (this.isMobileViewport) {
                    if (this.tabbables && this.tabbables.length && this.tabbables[0])
                        this.giveFocus(this.tabbables[0])
                } else {
                    if (this.tabbables && this.tabbables.length && this.tabbables.length > 1 && this.tabbables[1])
                        this.giveFocus(this.tabbables[1])
                    else
                        this.giveFocus(this.$refs.closeModal)
                }
            }
        },
    },

    beforeDestroy() {
        this.$eventbus.$off(this.$appevents.showModal)
        this.$eventbus.$off(this.$appevents.hideModal)
        // TODO: Keith to fix window wrapper package
        Window.removeEventListener('popstate', this.onBrowserBack)
    },

    beforeMount() {
        // TODO: Keith to fix window wrapper package
        Window.addEventListener('popstate', this.onBrowserBack)
    },

    created() {
        this.$eventbus.$on(this.$appevents.showModal, (contentComponent, {
            data = null,
            nestedComponent,
            cssModifier = '',
            onClose, onEdit, onCancel, onSave, editMode,
        } = {}) => {
            this._setModalContentComponents(contentComponent, editMode)
            this.modalInfoData = data
            this.nestedComponent = nestedComponent
            this.additionalCloseActions = onClose
            this.additionalEditActions = onEdit
            this.additionalCancelActions = onCancel
            this.additionalSaveActions = onSave
            this.cssModifier = cssModifier

            this.isShown = true
            this.preventAdditionalActions = false

            this.$eventbus.$emit(this.$appevents.hideSubMenu)
            this.$logger.log('Trying to show mask', loggingCategory)
            this.$eventbus.$emit(this.$appevents.showMask, 'under-modal', {
                onMaskClick: () => this.close(),
                cssModifier: `${ this.cssModifier } --modal-mask`,
            })

            this._setKeyboardNavigation()
            this.$logger.logObject(this.modalInfoData, 'Data passed to modal', loggingCategory)
        })

        this.$eventbus.$on(this.$appevents.hideModal, (afterFunc) => {
            this.close(null)
            this.preventAdditionalActions = false
            if (_.isFunction(afterFunc)) afterFunc()
        })

        this.$eventbus.$on(this.$appevents.hideModalWithoutActions, (afterFunc) => {
            this.preventAdditionalActions = true
            this.close(null)
            this.preventAdditionalActions = false
            if (_.isFunction(afterFunc)) afterFunc()
        })

        this.$logger.log('Modal component ready to receive events', loggingCategory)
    },

    methods: {

        /**
         * @param {{EditMode: Object}} component
         * @param {boolean} editMode
         * @private
         */
        _setModalContentComponents(component, editMode = false) {
            this.modalContentComponent = component
            this.readModalContentComponent = this.modalContentComponent
            this.editModalContentComponent = this.modalContentComponent.EditMode
                ? this.modalContentComponent.EditMode : this.modalContentComponent
            if (editMode) this.modalContentComponent = this.editModalContentComponent
        },

        /**
         * @private
         */
        _setKeyboardNavigation() {
            this.$nextTick(() => {
                this.$logger.logObject({ wrapper: this.$refs.modalWrapper, close: this.$refs.closeModal }, 'Tabbing control data passed', loggingCategory)
                this.tabbableRootNodeRef = this.$refs.modalWrapper
                this.bindTabbingEvents()
            })
        },

        close(ev) {
            if (this.isShown) {
                this.isShown = false
                this.modalContentComponent = undefined
                if (ev) ev.stopPropagation()
                this.$eventbus.$emit(this.$appevents.hideMask)
                this.$eventbus.$emit(this.$appevents.hideSubMenu)

                if (!this.preventAdditionalActions) {
                    if (_.isFunction(this.additionalCloseActions)) {
                        this.additionalCloseActions()
                    } else if (Window.getHref().endsWith('#')) {
                        this.preventAdditionalActions = true
                        history.back()
                    }
                    this.$eventbus.$emit(this.$appevents.afterModalClose)
                }
            }
        },

        doNothing(ev) {
            ev.stopPropagation()
        },

        onBrowserBack() {
            if (this.isShown && !this.preventAdditionalActions) {
                if (!Window.getHref().includes('#')) {
                    this.preventAdditionalActions = true
                    this.close(null)
                } else if (this.modalInfoData && this.modalInfoData.backSteps !== undefined && this.modalInfoData.backSteps < -1) {
                    history.go(this.modalInfoData.backSteps + 1)
                    this.preventAdditionalActions = true
                    this.close(null)
                }
            }
        },

        onEdit() {
            this.modalContentComponent = this.editModalContentComponent
            if (_.isFunction(this.additionalEditActions)) this.additionalEditActions()
        },

        onCancel() {
            this.modalContentComponent = this.readModalContentComponent
            if (_.isFunction(this.additionalCancelActions)) this.additionalCancelActions()
        },

        onSave(source, edits) {
            this.modalContentComponent = this.readModalContentComponent
            if (_.isFunction(this.additionalSaveActions)) this.additionalSaveActions(source, edits)
        },
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\app-core\_app-modal.scss

.modal {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    z-index: z-index('takeover-modal');
    margin-left: auto;
    margin-right: auto;
    overflow: hidden;
    @include offset-from-header('base', 'small', 'mid', 'wide');

    @include bp-min-small {
        z-index: z-index('modal');
    }

    @include element('content') {
        position: relative;
        display: block;
        width: 100%;
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        padding: rem(10px) rem(10px) rem(20px) rem(10px);

        @include bp-min-mid {
            max-width: 766px;
            height: auto;
            max-height: 95%;
            margin: 0 auto rem(55px);
            padding: rem(25px) rem(22px) rem(27px) rem(22px);
        }

        @include element('close') {
            display: none;

            @include bp-min-mid {
                display: block;
                position: absolute;
                right: 4px;
                top: 2px;
                font-size: 1.6rem;
                z-index: z-index('raised');

                &:hover, &:active {
                    cursor: pointer;
                    color: $error;
                }
            }
        }
    }

    // This is a custom modifier to add to modal content components that positions buttons appropriately for mobile view.
    .--modal-buttons {
        @include breakout-buttons;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;

        > button, a {
            flex: 1 0 100%;
        }

        @include bp-min-mid {
            @include clear-breakout-buttons;
            flex-direction: row;
            padding-top: 10px;
            justify-content: space-around;

            > button, a {
                flex: 0 1 auto;
            }
        }
    }

    @include responsive-type-modifiers;
}

</style>
